import { useState } from 'react';

// Util
import useDebounce from '../../../../../utils/react/useDebouncedValue';

// Types
import { BoardSearchResponse, BoardSearchSortMode } from '../../../../../../common/boards/search/boardSearchTypes';
import { BoardSearchResponseError } from '../boardSearchPopupTypes';

export type BoardSearchState = ReturnType<typeof useBoardSearchState>;

/**
 * Hook to manage the state of the board search popup.
 * A relatively simple "state container".
 * Logic for fetching and updating the search results is handled in `useBoardSearchPopupQueryController`
 */
const useBoardSearchState = () => {
    const [searchText, setSearchText] = useState('');
    const [searchSort, setSearchSort] = useState<BoardSearchSortMode>('viewed');
    const [scrollPosition, setScrollPosition] = useState(0);

    const [searchResponse, setSearchResponse] = useState<BoardSearchResponse>({
        results: [],
        offset: 0,
        total: 0,
        size: 0,
        sort: 'viewed',
        timestamp: 0,
    });

    const [searchResponseError, setSearchResponseError] = useState<BoardSearchResponseError | null>(null);

    const [isFetching, setIsFetching] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const debouncedQuery = useDebounce(searchText, 250);

    const clearResponse = () => {
        setSearchText('');
        setIsFetching(false);
        setSearchResponseError(null);
        setSearchResponse({
            results: [],
            offset: 0,
            total: 0,
            size: 0,
            sort: 'viewed',
            timestamp: 0,
        });
    };

    return {
        searchResponse,
        searchResponseError,
        searchText,
        searchSort,
        debouncedQuery,
        isFetching,
        isFetchingMore,
        scrollPosition,

        setSearchText,
        setSearchSort,
        setIsFetching,
        setIsFetchingMore,
        setSearchResponse,
        setSearchResponseError,
        setScrollPosition,
        clearResponse,
    };
};

export default useBoardSearchState;
